// A socket.io-compatible shim over a plain reconnecting WebSocket (partysocket), so
// the client can talk to the new Cloudflare Durable Objects game server (edge-server)
// with NO changes to RemoteGame.tsx's socket usage. It exposes just the slice of the
// socket.io API the client actually uses: on/emit/timeout().emit(ack)/removeAllListeners/
// disconnect, plus synthesized "connect"/"disconnect" events.
//
// Wire envelope is `{ type, ...payload }` JSON both directions (the same shape the
// dev connection.ts already uses, and what edge-server speaks). The server must put
// fields at the top level (e.g. {type:"game_update", data:{...}}) so the existing
// listeners that read m.data / m.matchConfig / m.gameState keep working unchanged.
import { WebSocket as PartyWebSocket } from "partysocket";

export interface SocketLike {
  connected: boolean;
  id?: string;
  on(event: string, cb: (msg: any) => void): SocketLike;
  off(event: string, cb?: (msg: any) => void): SocketLike;
  emit(event: string, payload?: any): SocketLike;
  timeout(ms: number): { emit(event: string, payload: any, ack: (err: any, res?: any) => void): void };
  removeAllListeners(): SocketLike;
  disconnect(): void;
}

// `url` may be a string, or an async resolver (used for public rooms, where the
// MatchRoom instance name is fetched from the matchmaker before connecting).
// Debug-only artificial network latency, opt-in via URL query so real play is
// never affected. Localhost has ~0ms latency, which hides every prediction /
// interpolation / death-timing bug; this lets us reproduce them on one machine.
//   ?netlag=150   simulated round-trip ping in ms (split evenly each direction)
//   ?netjitter=40 +/- ms of random variance per message (can reorder, like real UDP)
//   ?netloss=0.02 fraction of messages dropped (0..1)
// RTT measured by the client's rtt_ping then reflects ~netlag, so its latency
// compensation runs for real.
const NETLAG = (() => {
  if (typeof window === "undefined") return null;
  try {
    const q = new URLSearchParams(window.location.search);
    const ping = Number(q.get("netlag"));
    if (!isFinite(ping) || ping <= 0) return null;
    return {
      oneWay: ping / 2,
      jitter: Math.max(0, Number(q.get("netjitter")) || 0),
      loss: Math.min(1, Math.max(0, Number(q.get("netloss")) || 0)),
    };
  } catch {
    return null;
  }
})();

// Deliver a wire action now, or after the simulated one-way delay (dropping it
// on simulated loss). Used symmetrically on send and receive.
function netDeliver(fn: () => void) {
  if (!NETLAG) { fn(); return; }
  if (NETLAG.loss > 0 && Math.random() < NETLAG.loss) return; // packet lost
  const d = NETLAG.oneWay + (NETLAG.jitter ? (Math.random() * 2 - 1) * NETLAG.jitter : 0);
  setTimeout(fn, d > 0 ? d : 0);
}

export function createDsSocket(url: string | (() => Promise<string>)): SocketLike {
  const listeners = new Map<string, Set<(msg: any) => void>>();
  const pendingAcks = new Map<string, { cb: (e: any, r?: any) => void; timer: any }>();
  const sendBuffer: string[] = [];
  let ackSeq = 0;

  const shim: SocketLike = {
    connected: false,
    on(event, cb) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event)!.add(cb);
      return shim;
    },
    off(event, cb) {
      if (cb) listeners.get(event)?.delete(cb);
      else listeners.delete(event);
      return shim;
    },
    emit(event, payload) {
      rawSend({ type: event, ...spread(payload) });
      return shim;
    },
    timeout(ms) {
      return {
        emit(event, payload, ack) {
          const reqId = "a" + ++ackSeq;
          const timer = setTimeout(() => {
            pendingAcks.delete(reqId);
            ack(new Error("timeout"));
          }, ms);
          pendingAcks.set(reqId, { cb: ack, timer });
          rawSend({ type: event, reqId, ...spread(payload) });
        },
      };
    },
    removeAllListeners() {
      listeners.clear();
      return shim;
    },
    disconnect() {
      disposed = true;
      try { ws?.close(); } catch { /* already closed */ }
      shim.connected = false;
    },
  };

  // Resolve the URL (sync or async), then open the socket. Emits issued before this
  // resolves are buffered and flushed on open.
  let ws: any = null;
  let disposed = false; // disconnect() called before the (async) URL resolved
  Promise.resolve(typeof url === "function" ? url() : url)
    .then((resolved) => {
      // If we were disconnected while the URL (e.g. a matchmake fetch) was still
      // resolving, don't open a socket nobody will ever close - it would leak a live
      // WebSocket and a phantom member on the server room.
      if (disposed) return;
      ws = new PartyWebSocket(resolved);
      attachWs();
    })
    .catch((e) => { console.error("[ds-socket] could not resolve server URL", e); dispatch("disconnect", undefined); });

  function attachWs() {
    ws.addEventListener("open", () => {
      shim.connected = true;
      for (const s of sendBuffer.splice(0)) safeSend(s);
      dispatch("connect", undefined);
    });
    ws.addEventListener("close", () => {
      shim.connected = false;
      dispatch("disconnect", undefined);
    });
    ws.addEventListener("error", () => { /* partysocket auto-reconnects; surface nothing */ });
    ws.addEventListener("message", onMessage);
  }

  function onMessage(ev: any) {
    // Decode synchronously, but deliver after the simulated inbound delay.
    const raw = typeof ev.data === "string" ? ev.data : new TextDecoder().decode(ev.data);
    netDeliver(() => handleIncoming(raw));
  }
  function handleIncoming(raw: string) {
    let m: any;
    try { m = JSON.parse(raw); } catch { return; }
    if (!m || typeof m.type !== "string") return;
    // Ack reply for a timeout().emit() request (e.g. rtt_ping_ack).
    if (m.reqId && pendingAcks.has(m.reqId)) {
      const p = pendingAcks.get(m.reqId)!;
      clearTimeout(p.timer);
      pendingAcks.delete(m.reqId);
      p.cb(null, m);
      return;
    }
    dispatch(m.type, m);
  }

  function dispatch(event: string, msg: any) {
    const set = listeners.get(event);
    if (!set) return;
    for (const cb of [...set]) { try { cb(msg); } catch (e) { console.error("[ds-socket] listener error", e); } }
  }
  function safeSend(s: string) {
    if (!ws) { sendBuffer.push(s); return; }
    // Deliver after the simulated outbound delay (no-op without ?netlag).
    netDeliver(() => { try { ws.send(s); } catch { sendBuffer.push(s); } });
  }
  function rawSend(obj: any) {
    const s = JSON.stringify(obj);
    if (shim.connected) safeSend(s); else sendBuffer.push(s);
  }
  function spread(payload: any) { return payload && typeof payload === "object" ? payload : {}; }

  return shim;
}
